import { useAIChatFeatureFlags } from '@/hooks/useAIChatFeatureFlags';
import { useGettingStartedCompletionState } from '@/hooks/useGettingStartedCompletionState';

type PrecomputedCompletionState = Pick<
  ReturnType<typeof useGettingStartedCompletionState>,
  'gettingStartedState' | 'hasIncompleteSteps' | 'isLoading'
>;

/**
 * Single source of truth for "has this setup-assistant-only org finished
 * onboarding" — shared by every upgrade-nudge call site (TopNavBase's
 * nudge-on-open, CopilotKitAgentContext's guarded send, and the setup
 * assistant's own Upgrade CTA) so they can't disagree about completion state.
 * gettingStartedState !== null distinguishes "loaded, zero incomplete steps"
 * from "not fetched yet" (hasIncompleteSteps defaults to false while loading).
 * TODO(TF-677): replace with gigawatt_setup_assistant entitlement once core-entity ships it
 *
 * Callers that already fetch getting-started completion state for another
 * reason (e.g. CopilotKitAgentProvider, which needs it for agent context)
 * can pass that result in as `precomputedState` to avoid running the
 * underlying queries a second time.
 */
export const useSetupAssistantUpgradeState = (
  precomputedState?: PrecomputedCompletionState
) => {
  const { isSetupAssistantOnlyOrg } = useAIChatFeatureFlags();
  const ownState = useGettingStartedCompletionState({
    enabled: !precomputedState && isSetupAssistantOnlyOrg,
  });
  const { gettingStartedState, hasIncompleteSteps, isLoading } =
    precomputedState ?? ownState;

  const setupFullyComplete =
    isSetupAssistantOnlyOrg &&
    !isLoading &&
    gettingStartedState !== null &&
    !hasIncompleteSteps;

  return { isSetupAssistantOnlyOrg, setupFullyComplete };
};
